import React, { Suspense, lazy } from "react";
import { clearAuth, getCurrentUser, getRefreshToken } from "../auth";
import { revokeRefreshTokenApi } from "../api/loginApi";
import { Route, Routes, Navigate } from "react-router-dom";
import { Spinner } from "react-bootstrap";
import NotFound from "../../components/notFound/notFound";
import Forbidden from "../../components/forbidden/Forbidden";
import Header from '../../components/Header/Header';
import Footer from '../../components/Footer/Footer';
import { permissionModules } from "../../utill/config";

// Feature pages are lazy so the initial bundle only carries the shell
// (Header/Footer/Login + router itself). Each chunk loads on first
// navigation to that route and is cached by the browser afterwards.
const Login = lazy(() => import("../../components/Login"));
const Schedules = lazy(() => import("../../components/Schedules/Schedules"));
const ScheduleDetail = lazy(() => import("../../components/ScheduleDetail"));
const Files = lazy(() => import("../../components/Files/Files"));
const Profile = lazy(() => import("../../components/Profile/Profile"));
const Admin = lazy(() => import("../../components/Admin/Admin"));
const Sync = lazy(() => import("../../components/Sync/Sync"));
const DiagnosticsView = lazy(() => import("../../components/DiagnosticsView/DiagnosticsView"));
const Dashboard = lazy(() => import("../../components/Dashboard/Dashboard"));
const ChangePassword = lazy(() => import("../../components/ChangePassword/ChangePassword"));

const RouteFallback = () => (
  <div className="route-fallback d-flex justify-content-center align-items-center" style={{ minHeight: "40vh" }}>
    <Spinner animation="border" variant="primary" role="status" />
  </div>
);

const Layout = ({ children }) => {
  if (!localStorage.token || !localStorage.currentUser) {
    clearAuth();
    return <Navigate to="/login" replace />;
  }
  // Force-change guard. If the backend has flagged this user as needing
  // a credential change (set on first login or after admin reset), every protected
  // route bounces to the forced-change page until they pick a new one. The page
  // itself isn't wrapped in Layout (no nav, no footer), so it doesn't loop.
  // Backend defense-in-depth: AuthenticationFilter blocks every other endpoint
  // with 403 ERR_GL023 anyway — this is the UX layer.
  const currentUser = getCurrentUser();
  if (currentUser && currentUser.mustChangePassword) {
    return <Navigate to="/change-password" replace />;
  }
  return (
    <div className="app-shell">
      <Header />
      <main className="main-content">
        <Suspense fallback={<RouteFallback />}>{children}</Suspense>
      </main>
      <Footer />
    </div>
  );
};

const Logout = () => {
  // Fire-and-forget server-side revocation. The local clearAuth + navigate happens
  // synchronously so the UX is the same whether the network call succeeds, fails,
  // or hasn't returned yet — the refresh token will expire on its own within 30
  // days at worst. Doing this before clearAuth so the token value is still readable.
  const refreshToken = getRefreshToken();
  if (refreshToken) {
    revokeRefreshTokenApi(refreshToken);
  }
  clearAuth();
  return <Navigate to="/login" replace />;
};

const hasPermission = (module, verb = "view") => {
  try {
    const currentUser = getCurrentUser();
    return (
      currentUser &&
      currentUser.userType &&
      permissionModules[currentUser.userType] &&
      permissionModules[currentUser.userType][module] &&
      permissionModules[currentUser.userType][module].includes(verb)
    );
  } catch (e) {
    return false;
  }
};

// Renders <Forbidden /> (403) when the user lacks the verb on the module — not
// <NotFound /> (404). The path exists; this user is just not allowed to load it.
// The previous behaviour leaked the (small but real) signal that a route is a
// 404 across the whole app, so a logged-in user couldn't distinguish "wrong URL"
// from "you can't see this".
const Gated = ({ module, verb = "view", children }) => {
  return hasPermission(module, verb) ? children : <Forbidden />;
};

class Router extends React.Component {
  render() {
    const schedulesEl = <Layout><Schedules /></Layout>;
    const adminEl = (
      <Layout>
        <Gated module="admin"><Admin /></Gated>
      </Layout>
    );
    const syncEl = (
      <Layout>
        <Gated module="sync"><Sync /></Gated>
      </Layout>
    );
    return (
      <Routes>
        <Route
          path="/login"
          element={<Suspense fallback={<RouteFallback />}><Login /></Suspense>}
        />
        <Route path="/dashboard" element={<Layout><Dashboard /></Layout>} />
        <Route path="/" element={schedulesEl} />
        <Route path="/schedules" element={schedulesEl} />
        <Route path="/schedules/:scheduleId/:type" element={<Layout><ScheduleDetail /></Layout>} />
        <Route
          path="/files"
          element={
            <Layout>
              <Gated module="file"><Files /></Gated>
            </Layout>
          }
        />
        <Route path="/admin/*" element={adminEl} />
        <Route path="/sync/*" element={syncEl} />
        <Route path="/profile" element={<Layout><Profile /></Layout>} />
        <Route
          path="/diagnostics/:scheduleType/:scheduleId/:vehicleId"
          element={<Layout><DiagnosticsView /></Layout>}
        />
        <Route path="/logout" element={<Logout />} />
        {/* Forced-change page is intentionally NOT wrapped in Layout (no nav /
            footer) and not gated — the Layout guard would loop us back here
            forever. The page itself checks for a token + currentUser. */}
        <Route
          path="/change-password"
          element={
            !localStorage.token || !localStorage.currentUser
              ? <Navigate to="/login" replace />
              : <Suspense fallback={<RouteFallback />}><ChangePassword /></Suspense>
          }
        />
        <Route path="*" element={<Layout><NotFound /></Layout>} />
      </Routes>
    );
  }
}

export default Router;
